// Shared, isomorphic pricing model for the Palworld TCG Price Guide (/prices).
//
// Two data layers feed the price board:
//  1. LIVE SNAPSHOTS — rows in public.card_price_points / public.sealed_price_points,
//     written once a day by the automated sync job (src/lib/prices.server.ts). Any
//     marketplace feed we can legally read (eBay Browse API, TCGplayer, Cardmarket,
//     PriceCharting) lands here keyed by `source`, one row per card per source per day.
//  2. PAL STARS INDEX — a transparent, deterministic baseline computed from the printed
//     rarity, set, chase status and playability of each card. It exists so every one of
//     the 244 indexed cards has a comparable value even before a marketplace has a
//     listing for it, and it is always labelled as modelled in the UI.
//
// Marketplaces are registered ONCE here and deduped by hostname, so a card never shows
// the same storefront twice even when several guides or feeds reference it.

import { OFFICIAL_CARDS, type OfficialCard, type OfficialRarity } from "./tcg-cards";
import { ebayAffiliateUrl } from "./ebay-affiliate";
import { tcgplayerAffiliateUrl } from "./tcgplayer-affiliate";



/* ------------------------------------------------------------------ sources */

export type SourceKind = "asking" | "sold" | "both";

export interface PriceSource {
  /** Stable id, also used as the `source` column value for live snapshots. */
  id: string;
  label: string;
  host: string;
  region: "US" | "EU" | "UK" | "JP" | "Global";
  kind: SourceKind;
  /** True when the site publishes completed-sale (sold) data. */
  note: string;
  /** Deep link for a single card. */
  card: (c: OfficialCard) => string;
  /** Deep link for a sealed product query. */
  sealed?: (query: string) => string;
}

const q = (s: string) => encodeURIComponent(s);

/**
 * Pal Stars only prices English printings, so every English-market eBay deep
 * link carries the same language exclusions the ingestion filter applies.
 */
export const EN_ONLY_TERMS = " -japanese -korean -chinese -jpn -jp -kor -kr -cn";
/** Encode a search phrase restricted to English printings. */
const qe = (s: string) => encodeURIComponent(s + EN_ONLY_TERMS);
/** Append the English-only exclusions to a raw eBay search phrase. */
export const EN_ONLY = (s: string): string => s + EN_ONLY_TERMS;

/** Search phrase used on generic marketplaces: name + printed code. */
export function cardQuery(c: OfficialCard): string {
  return `Palworld ${c.name} ${c.code}`;
}

const RAW_SOURCES: PriceSource[] = [
  {
    id: "ebay",
    label: "eBay — live asking",
    host: "www.ebay.com",
    region: "US",
    kind: "asking",
    note: "Active listings, Buy It Now and auctions, sorted by price.",
    card: (c) => `https://www.ebay.com/sch/i.html?_nkw=${qe(cardQuery(c))}&_sop=15`,
    sealed: (s) => `https://www.ebay.com/sch/i.html?_nkw=${qe(s)}&_sop=15`,
  },
  {
    id: "tcgplayer-sold",
    label: "TCGplayer — sold prices",
    host: "www.tcgplayer.com/sold",
    region: "US",
    kind: "sold",
    note: "TCGplayer market price and recent sales history per printing.",
    card: (c) =>
      tcgplayerAffiliateUrl(
        `https://www.tcgplayer.com/search/all/product?q=${q(`${c.name} ${c.code}`)}&view=grid`,
        c.code,
      ),
    sealed: (s) => tcgplayerAffiliateUrl(`https://www.tcgplayer.com/search/all/product?q=${q(s)}&view=grid`, s),
  },
  {
    id: "tcgplayer",
    label: "TCGplayer — asking",
    host: "www.tcgplayer.com",
    region: "US",
    kind: "asking",
    note: "The US singles marketplace standard: seller stock and asking prices.",
    card: (c) =>
      tcgplayerAffiliateUrl(`https://www.tcgplayer.com/search/all/product?q=${q(`${c.name} ${c.code}`)}`, c.code),
    sealed: (s) => tcgplayerAffiliateUrl(`https://www.tcgplayer.com/search/all/product?q=${q(s)}`, s),
  },

  {
    id: "ebay-uk",
    label: "eBay UK — live asking",
    host: "www.ebay.co.uk",
    region: "UK",
    kind: "asking",
    note: "UK active listings in GBP, converted to USD.",
    card: (c) => `https://www.ebay.co.uk/sch/i.html?_nkw=${qe(cardQuery(c))}&_sop=15`,
    sealed: (s) => `https://www.ebay.co.uk/sch/i.html?_nkw=${qe(s)}`,
  },
  {
    id: "ebay-graded",
    label: "eBay — graded slabs (PSA/BGS/CGC)",
    host: "www.ebay.com/graded",
    region: "US",
    kind: "asking",
    note: "Certified slabs only. Graded copies trade far above raw, so they are tracked and shown separately instead of blended into the raw market price.",
    card: (c) => `https://www.ebay.com/sch/i.html?_nkw=${qe(`${cardQuery(c)} PSA`)}&_sop=15`,
  },
  {
    // Asian-language printings. Deliberately NOT English-filtered: this link is
    // purchase-only and never participates in English pricing calculations.
    id: "ebay-jp",
    label: "eBay — Japanese, Chinese & Korean printings",
    host: "www.ebay.com/jp",
    region: "JP",
    kind: "asking",
    note: "Japanese, Chinese and Korean printings on eBay — auctions and Buy It Now. These listings never affect the English market price or index.",
    card: (c) =>
      `https://www.ebay.com/sch/i.html?_nkw=${q(`Palworld ${c.name} ${c.code.replace(/^E/i, "")} (Japanese,Chinese,Korean,JP,CN,KR)`)}&_sop=15`,
    sealed: (s) => `https://www.ebay.com/sch/i.html?_nkw=${q(`${s} (Japanese,Chinese,Korean,JP,CN,KR)`)}&_sop=15`,
  },
];


/** Sources that quote certified (graded) copies rather than raw singles. */
export const GRADED_SOURCE_IDS = new Set(["ebay-graded"]);

export const isGradedSource = (id: string): boolean => GRADED_SOURCE_IDS.has(id);

/**
 * Sources that quote JAPANESE printings. A Japanese card has its own set codes,
 * print run and demand curve, so these quotes are tracked as their own series
 * and never blended into the English market value, the English low/high band or
 * the published base-1000 index.
 */
export const JP_SOURCE_IDS = new Set(["ebay-jp", "ebay-jp-sold"]);

export const isJapaneseSource = (id: string): boolean => JP_SOURCE_IDS.has(id);

/**
 * Platform of a stored quote. eBay and TCGplayer are kept apart everywhere:
 * buyer behaviour differs on each platform, so blending them into one index
 * would publish a number neither audience recognises.
 */
export type PricePlatform = "ebay" | "tcgplayer";

export const isEbaySource = (id: string): boolean => id.startsWith("ebay");
export const isTcgplayerSource = (id: string): boolean => id.startsWith("tcgplayer");
export const isSoldSource = (id: string): boolean => id.endsWith("-sold");
export const sourcePlatform = (id: string): PricePlatform | null =>
  isTcgplayerSource(id) ? "tcgplayer" : isEbaySource(id) ? "ebay" : null;



/**
 * Deduped registry — one entry per source id, first registration wins. Every
 * eBay deep link is wrapped in EPN affiliate tagging on the way out, so a card
 * a visitor buys through the price board is credited to Pal Stars.
 */
export const PRICE_SOURCES: PriceSource[] = (() => {
  const seen = new Set<string>();
  const out: PriceSource[] = [];
  for (const s of RAW_SOURCES) {
    const key = s.id.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      ...s,
      // The card code / product query becomes the EPN sub-id, so a commission
      // reported by EPN can be matched back to the exact click target.
      card: (c) => ebayAffiliateUrl(s.card(c), c.code),
      ...(s.sealed ? { sealed: (query: string) => ebayAffiliateUrl(s.sealed!(query), query) } : {}),
    });
  }
  return out;
})();


export const SOURCE_BY_ID: Record<string, PriceSource> = Object.fromEntries(
  PRICE_SOURCES.map((s) => [s.id, s]),

);

/* ------------------------------------------------- graded population reports */

/**
 * Graded population reports. These are NOT price feeds — they report how many
 * copies of a card each grading company has certified at every grade, which is
 * the scarcity half of a graded card's value. Registered once and deduped by
 * host so a card never shows the same grader twice.
 */
export interface GradingSource {
  id: string;
  /** Grading company name as printed on the slab label. */
  company: string;
  label: string;
  host: string;
  /** Grading scale summary shown under the link. */
  scale: string;
  note: string;
  /** Population-report entry point. */
  pop: string;
}

const RAW_GRADERS: GradingSource[] = [
  {
    id: "psa",
    company: "PSA",
    label: "PSA — graded population report",
    host: "www.psacard.com",
    scale: "1–10, PSA 10 Gem Mint",
    note: "PSA's population report: how many Palworld cards PSA has certified at each grade. Search the printed card number (for example EBP01-025SSP) to see the pop at PSA 9 and PSA 10 before you pay a graded premium.",
    pop: "https://www.psacard.com/pop",
  },
  {
    id: "beckett",
    company: "Beckett (BGS)",
    label: "Beckett — graded pop & price guide",
    host: "www.beckett.com",
    scale: "Half steps to 10, four subgrades, Black Label",
    note: "Beckett's online population report and price-guide subscription covering sports and non-sports cards. Beckett prints centering, corners, edges and surface subgrades, so a BGS 9.5 and a Black Label 10 are priced very differently.",
    pop: "https://www.beckett.com/grading/population-report",
  },
  {
    id: "cgc",
    company: "CGC Cards",
    label: "CGC — EBP01 Dawn of Palpagos pop report",
    host: "www.cgccards.com",
    scale: "1–10, CGC 10 Pristine / Perfect 10",
    note: "CGC Cards' English card population report for Palworld, including (EBP01) Dawn of Palpagos. CGC pops run smaller than PSA on modern releases, so a low CGC 10 count does not always mean the card is scarce overall.",
    pop: "https://www.cgccards.com/population-report/tcg/palworld/1/",
  },
];

/** Deduped grader registry — one entry per company host. */
export const GRADING_SOURCES: GradingSource[] = (() => {
  const seen = new Set<string>();
  const out: GradingSource[] = [];
  for (const g of RAW_GRADERS) {
    const key = g.host.toLowerCase().replace(/^www\./, "");
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(g);
  }
  return out;
})();


/* --------------------------------------------------------- sealed products */

export type SealedCategory = "pack" | "box" | "deck" | "case" | "merch";

export interface SealedProduct {
  key: string;
  name: string;
  detail: string;
  /** Official retail price in USD, used as the anchor and sanity band centre. */
  msrp: number;
  /** Marketplace search phrase for the deep links and the live feed. */
  query: string;
  category: SealedCategory;
  /**
   * Every pattern must match a listing title for the quote to be sampled. This
   * is what keeps a booster-box price out of the single-pack row and a plush
   * price out of the Trial Deck row.
   */
  match: RegExp[];
  /** Patterns that disqualify a listing for THIS product (bundles of others). */
  exclude?: RegExp[];
}

export const SEALED_CATEGORY_LABEL: Record<SealedCategory, string> = {
  pack: "Booster packs",
  box: "Booster boxes",
  deck: "Trial decks",
  case: "Sealed cases",
  merch: "Merchandise",
};

const BOX = /\bbooster box\b|\bdisplay box\b/;
const PACK = /\b(?:booster )?pack\b/;
const DECK = /\b(?:trial|starter) deck\b/;

export const SEALED_PRODUCTS: SealedProduct[] = [
  // ---------------------------------------------------------------- packs
  // Single units only. Multi-item lots ("2x booster box", "24 pack lot") are
  // rejected by the validator, so they are not tracked as their own rows.
  { key: "bp01-pack", name: "Booster Pack — Dawn of Palpagos", detail: "EBP01 · single pack", msrp: 4.99, category: "pack", query: "Palworld TCG booster pack Dawn of Palpagos EBP01", match: [PACK, /\b(?:e?bp01|dawn of palpagos)\b/], exclude: [BOX, /\bcase\b|\bdeck\b/] },
  { key: "bp02-pack", name: "Booster Pack — Legends Awaken", detail: "EBP02 · single pack", msrp: 4.99, category: "pack", query: "Palworld TCG booster pack Legends Awaken EBP02", match: [PACK, /\b(?:e?bp02|legends awaken)\b/], exclude: [BOX, /\bcase\b|\bdeck\b/] },
  // ---------------------------------------------------------------- boxes
  { key: "bp01-box", name: "Booster Box — Dawn of Palpagos", detail: "EBP01 · 12 packs · 84 cards", msrp: 59.99, category: "box", query: "Palworld TCG booster box Dawn of Palpagos EBP01", match: [BOX, /\b(?:e?bp01|dawn of palpagos)\b/], exclude: [/\bcase\b/, DECK] },
  { key: "bp02-box", name: "Booster Box — Legends Awaken", detail: "EBP02 · 12 packs", msrp: 59.99, category: "box", query: "Palworld TCG booster box Legends Awaken EBP02", match: [BOX, /\b(?:e?bp02|legends awaken)\b/], exclude: [/\bcase\b/, DECK] },
  // ---------------------------------------------------------------- decks
  { key: "td01-deck", name: "Trial Deck — Red・Blue", detail: "ETD01 · ready to play", msrp: 14.99, category: "deck", query: "Palworld TCG trial deck Red Blue ETD01", match: [DECK, /\bred\b|\betd?01\b/], exclude: [BOX, /\bcase\b|\bbundle\b/] },
  { key: "td02-deck", name: "Trial Deck — Green・Purple", detail: "ETD02 · ready to play", msrp: 14.99, category: "deck", query: "Palworld TCG trial deck Green Purple ETD02", match: [DECK, /\bgreen\b|\betd?02\b/], exclude: [BOX, /\bcase\b|\bbundle\b/] },
  // Eternal Ascent trial decks (ETD03 / ETD04) — the second trial-deck wave.
  { key: "td03-deck", name: "Trial Deck — Eternal Ascent Red・Green", detail: "ETD03 · Eternal Ascent · ready to play", msrp: 16.99, category: "deck", query: "Palworld TCG Eternal Ascent trial deck Red Green ETD03", match: [DECK, /\beternal ascent\b|\betd?03\b/], exclude: [BOX, /\bcase\b|\bbundle\b/] },
  { key: "td04-deck", name: "Trial Deck — Eternal Ascent Blue・Purple", detail: "ETD04 · Eternal Ascent · ready to play", msrp: 16.99, category: "deck", query: "Palworld TCG Eternal Ascent trial deck Blue Purple ETD04", match: [DECK, /\beternal ascent\b|\betd?04\b/], exclude: [BOX, /\bcase\b|\bbundle\b/] },
  // ---------------------------------------------------------------- cases
  { key: "case", name: "Sealed Case — Dawn of Palpagos", detail: "EBP01 · 16 boxes", msrp: 899, category: "case", query: "Palworld TCG sealed case booster boxes EBP01", match: [/\bcase\b/, /\bbooster\b/, /\b(?:e?bp01|dawn of palpagos)\b/], exclude: [/\b(?:e?bp02|legends awaken)\b/] },
  { key: "bp02-case", name: "Sealed Case — Legends Awaken", detail: "EBP02 · 16 boxes", msrp: 899, category: "case", query: "Palworld TCG sealed case booster boxes Legends Awaken EBP02", match: [/\bcase\b/, /\bbooster\b/, /\b(?:e?bp02|legends awaken)\b/] },
  // ------------------------------------------------------------ merchandise
  { key: "merch-plush", name: "Palworld plush", detail: "Official plush toys", msrp: 24.99, category: "merch", query: "Palworld official plush", match: [/\bplush(?:ie)?\b/], exclude: [/\bbundle\b|\bset of\b/] },
  { key: "merch-figure", name: "Palworld figure", detail: "Official figures & statues", msrp: 39.99, category: "merch", query: "Palworld official figure", match: [/\bfigure|\bstatue\b|\bfigurine\b/], exclude: [/\bbundle\b|\bset of\b/] },
  { key: "merch-playmat", name: "TCG playmat", detail: "Official card game playmat", msrp: 24.99, category: "merch", query: "Palworld card game playmat", match: [/\bplay ?mat\b/], exclude: [BOX, /\bcase\b|\bbundle\b|\bdeck\b/] },
  { key: "merch-sleeves", name: "TCG card sleeves", detail: "Official deck protectors", msrp: 9.99, category: "merch", query: "Palworld card game sleeves", match: [/\bsleeves?\b/], exclude: [BOX, /\bcase\b|\bbundle\b|\bdeck\b|\bbinder\b/] },
  { key: "merch-binder", name: "TCG binder & storage", detail: "Official binder / storage box", msrp: 19.99, category: "merch", query: "Palworld card game binder", match: [/\bbinder\b|\bstorage box\b|\bportfolio\b/], exclude: [BOX, /\bcase of\b|\bbundle\b/] },
  { key: "merch-apparel", name: "Palworld apparel", detail: "Official shirts & hoodies", msrp: 29.99, category: "merch", query: "Palworld official shirt hoodie", match: [/\b(?:t[- ]?shirt|shirt|hoodie|sweatshirt)\b/], exclude: [/\bbundle\b/] },
  { key: "merch-sleeve-set", name: "Sleeve & Card Set Vol. 1", detail: "Official Card Game · sleeves + promo card set", msrp: 14.99, category: "merch", query: "Palworld Official Card Game Sleeve & Card Set Vol. 1", match: [/\bsleeve\b/, /\b(?:card set|vol\.? ?1|volume 1)\b/], exclude: [BOX, /\bcase of\b|\bbundle\b/] },
  { key: "merch-legends-awaken", name: "Legends Awaken merchandise", detail: "EBP02 accessories, sleeves & playmats", msrp: 24.99, category: "merch", query: "Palworld Legends Awaken merchandise accessories", match: [/\blegends awaken\b/, /\bsleeves?\b|\bplay ?mat\b|\bdeck ?box\b|\bstorage box\b|\bbinder\b|\bplush\b|\bkey ?(?:chain|ring)\b|\bacrylic\b/], exclude: [BOX, /\bcase of\b|\bbundle\b/] },
  { key: "merch-keychain", name: "Palworld keychains & pins", detail: "Official small goods", msrp: 12.99, category: "merch", query: "Palworld official keychain pin", match: [/\bkey ?(?:chain|ring)\b|\bpin badge\b|\bacrylic\b/], exclude: [/\bbundle\b|\bset of\b/] },
];

export const SEALED_BY_KEY: Record<string, SealedProduct> = Object.fromEntries(
  SEALED_PRODUCTS.map((p) => [p.key, p]),
);

/** Titles that never describe genuine sealed product or official merchandise. */
const SEALED_REJECT: RegExp[] = [
  /\bempty\b/,
  /\bwrapper(?:s)? only\b/,
  /\bopened\b/,
  /\bno cards\b/,
  /\bprox(?:y|ies)\b/,
  /\b(?:custom|orica|fan[\s-]?made|bootleg|replica)\b/,
  /\bdigital\b/,
  /\bcode\b/,
  /\bpre[\s-]?order\b/,
  /\bpsa|\bbgs\b|\bcgc\b/,
  // Multi-item lots are the main way a sealed median goes wrong: a "10x booster
  // pack lot" is not a single pack price.
  /\bjob lot\b|\blots?\b/,
  /\b(?:x\s?\d{1,3}|\d{1,3}\s?x)\b/,
  /\b(?:2|3|4|5|6|8|10|12|16|20|24|36)\s+(?:packs?|boxes|decks?|cases?)\b/,
];

/**
 * True when a listing title plausibly describes the given sealed product or
 * merchandise item. Sealed rows are only as honest as this filter: without it a
 * loose single or a plush lands in the booster-box median.
 */
export function isSealedListing(title: string, p: SealedProduct): boolean {
  const t = title.toLowerCase();
  if (!t.includes("palworld") && !t.includes("パルワールド")) return false;
  if (SEALED_REJECT.some((re) => re.test(t))) return false;
  if (p.category !== "merch" && !/\b(?:tcg|card game|trading card|ccg)\b/.test(t) && !/\b(?:pack|box|deck|case)\b/.test(t)) {
    return false;
  }
  if (p.exclude?.some((re) => re.test(t))) return false;
  return p.match.every((re) => re.test(t));
}

/** Sanity band for a sealed quote: catches data errors, not real premiums. */
export function isPlausibleSealedQuote(usd: number, p: SealedProduct): boolean {
  if (!Number.isFinite(usd) || usd < 1) return false;
  // Sealed product does trade over MSRP, but a 5x asking price on a $15 deck is
  // a bundle or a mispriced listing, not the market.
  const ceiling = p.category === "case" ? 2.5 : p.category === "merch" ? 3 : 3.5;
  return usd >= p.msrp * 0.4 && usd <= p.msrp * ceiling;
}

/** One stored sealed snapshot row. */
export interface SealedSnapshot {
  product_key: string;
  source: string;
  day: string;
  market: number | null;
  low: number | null;
  high: number | null;
  sold_avg: number | null;
  sold_count: number;
  listings: number;
}

export interface SealedRow extends SealedProduct {
  /** Best live asking quote in USD, null when only the MSRP anchor exists. */
  market: number | null;
  low: number | null;
  high: number | null;
  soldAvg: number | null;
  soldCount: number;
  listings: number;
  /** Day of the newest live quote used. */
  day: string | null;
  sources: string[];
}

/** Collapses stored sealed snapshots into one row per tracked product. */
export function buildSealedRows(snapshots: SealedSnapshot[]): SealedRow[] {
  const byKey = new Map<string, SealedSnapshot[]>();
  for (const s of snapshots) {
    const list = byKey.get(s.product_key) ?? [];
    list.push(s);
    byKey.set(s.product_key, list);
  }
  return SEALED_PRODUCTS.map((p) => {
    const rows = (byKey.get(p.key) ?? []).filter((r) => r.source !== INDEX_SOURCE_ID);
    // Newest day only, so a stale quote never blends with a fresh one.
    const newest = rows.map((r) => r.day).sort().at(-1) ?? null;
    const live = rows.filter((r) => r.day === newest);
    const asking = live
      .map((r) => r.market)
      .filter((n): n is number => typeof n === "number" && n > 0 && isPlausibleSealedQuote(n, p));
    const sold = live
      .map((r) => r.sold_avg)
      .filter((n): n is number => typeof n === "number" && n > 0 && isPlausibleSealedQuote(n, p));
    const q = robustQuote(asking, 1);
    const soldQ = robustQuote(sold, 1);
    return {
      ...p,
      market: q?.market ?? null,
      low: q?.low ?? null,
      high: q?.high ?? null,
      soldAvg: soldQ?.market ?? null,
      soldCount: live.reduce((a, r) => a + (r.sold_count || 0), 0),
      listings: live.reduce((a, r) => a + (r.listings || 0), 0),
      day: asking.length || sold.length ? newest : null,
      sources: [...new Set(live.map((r) => r.source))],
    };
  });
}


/* ------------------------------------------------------------ index model */

/**
 * Baseline USD market value by printed rarity (Pal Stars Index anchor).
 * Calibrated against the median validated eBay US/UK asking price per rarity,
 * so the modelled fallback sits on the real market instead of theory — commons
 * never actually change hands under ~$2 once a seller lists them singly, and
 * OSR parallels trade far below their hype price.
 */
// Recalibrated 2026-09-11 against the per-rarity median of every validated
// eBay US/UK and TCGplayer quote on the board (scripts/base-audit.ts). TCGplayer
// singles pricing showed commons/uncommons/trial-deck cards trade as bulk
// (~$0.25), while SP/SSP/TSP parallels ask well above the old anchors.
const RARITY_BASE: Record<OfficialRarity, number> = {
  C: 0.25,
  U: 0.22,
  R: 0.88,
  RR: 4.5,
  SR: 4.65,
  SP: 162,
  SSP: 343,
  OSR: 22.3,
  SSS: 2400,
  TD: 0.63,
  TSR: 26.5,
  TSP: 573,
  PR: 55,
};


/** Cards whose gameplay demand adds a premium on top of the rarity anchor. */
const META_PREMIUM = new Set(["Jormuntide Ignis", "Suzaku", "Bushi", "Grizzbolt", "Chillet", "Frostallion", "Paladius", "Necromus", "Blazamut", "Anubis", "Lyleen", "Faleris", "Shadowbeak", "Cattiva"]);

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967295;
}

export function utcDay(offset = 0): string {
  const d = new Date(Date.now() + offset * 86400000);
  return d.toISOString().slice(0, 10);
}

/** Deterministic, transparent baseline value for a card in USD. */
export function indexValue(c: OfficialCard): number {
  let v = RARITY_BASE[c.rarity] ?? 1;
  if (META_PREMIUM.has(c.name)) v *= 1.45;
  if (c.lucky) v *= 1.3;
  if (c.type === "Soul") v *= 1.2;
  if (c.type === "Gear" || c.type === "Structure") v *= 0.85;
  // ±18% spread so equal-rarity cards aren't a flat line, seeded by card code.
  v *= 0.82 + hash(c.code) * 0.36;
  return round2(v);
}

/** One multiplication step behind a card's Pal Stars Index baseline. */
export interface IndexStep {
  label: string;
  detail: string;
  factor: number;
  value: number;
}

/**
 * Show-your-work breakdown of `indexValue`: the printed-rarity anchor and every
 * multiplier applied to it, so a collector can audit the baseline instead of
 * taking it on trust.
 */
export function indexBreakdown(c: OfficialCard): { steps: IndexStep[]; total: number } {
  const steps: IndexStep[] = [];
  let v = RARITY_BASE[c.rarity] ?? 1;
  steps.push({
    label: `Rarity anchor (${c.rarity})`,
    detail: "Starting USD anchor for this printed rarity, set from observed sale ranges for the rarity.",
    factor: 1,
    value: round2(v),
  });
  const mult = (label: string, detail: string, factor: number) => {
    v *= factor;
    steps.push({ label, detail, factor, value: round2(v) });
  };
  if (META_PREMIUM.has(c.name)) mult("Meta demand", `${c.name} is played in competitive lists, so gameplay demand adds a premium.`, 1.45);
  if (c.lucky) mult("Lucky printing", "Lucky Pal printings are pulled at a lower rate and carry a collector premium.", 1.3);
  if (c.type === "Soul") mult("Soul card", "Soul cards are consumed across many decks, lifting steady demand.", 1.2);
  if (c.type === "Gear" || c.type === "Structure") mult("Gear / Structure", "Gear and Structure cards see thinner collector demand than Pals.", 0.85);
  mult(
    "Per-card spread",
    "Deterministic ±18% spread seeded by the card code, so equal-rarity printings are not a flat line.",
    round2(0.82 + hash(c.code) * 0.36),
  );
  return { steps, total: round2(v) };
}

/** Deterministic day-over-day movement, so charts are stable between renders. */
function drift(code: string, day: string): number {
  return (hash(`${code}:${day}`) - 0.5) * 0.14;
}

export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}




/* ------------------------------------------------- quote validation layer */
//
// Marketplace search results are noisy: bundles, proxies, graded slabs, sealed
// product and wrong-game hits all land in the same result set. Every quote is
// filtered and normalised through the helpers below BEFORE it is stored, which
// is what keeps the "sold" column honest. The same rules run in the UI when we
// decide whether a stored quote is trustworthy enough to move the market price.

/** Indicative FX to USD. Quotes in other currencies are normalised on ingest. */
export const FX_TO_USD: Record<string, number> = {
  USD: 1,
  EUR: 1.09,
  GBP: 1.28,
  JPY: 1 / 152,
  CAD: 0.73,
  AUD: 0.66,
};

export function toUsd(value: number, currency = "USD", rates?: Record<string, number>): number {
  const key = currency.toUpperCase();
  const rate = rates?.[key] ?? FX_TO_USD[key] ?? 1;
  return round2(value * rate);
}

/**
 * Titles that are never a single raw copy of the card we asked for.
 *
 * These MUST be anchored patterns, not bare substrings. A substring list is how
 * a price board goes quietly wrong in one direction: "lot" also matches "slot",
 * "case" matches "toploader case" and "showcase" (a normal single), "pack "
 * matches the extremely common "pack fresh", and "sale" matches "SALE" on any
 * discounted listing. Every one of those false rejects removes a cheap, legit
 * single and drags the median upward, which is exactly the inflated asking
 * price users were seeing on thin cards.
 */
const REJECT_PATTERNS: RegExp[] = [
  /\blots?\b/,
  /\bbundles?\b/,
  /\bplayset\b/,
  /\bset of\b/,
  /\b(?:x\s?\d{1,3}|\d{1,3}\s?x)\s+cards?\b/,
  /\b\d{2,3}\s+cards?\b/,
  /\bbulk\b/,
  /\bprox(?:y|ies)\b/,
  /\b(?:custom|orica|fan[\s-]?made|homemade)\b/,
  /\bsticker\b/,
  /\bsleeves?\b/,
  /\bbinder\b/,
  /\btoploader pack\b/,
  /\bbooster (?:box|pack|bundle)\b/,
  /\bbooster\b(?!.*\bsingle\b)/,
  /\belite trainer\b/,
  /\bsealed\b/,
  /\bcase of\b/,
  /\b(?:psa|bgs|cgc|sgc|ace|tag)\s?\d/,
  /\b(?:psa|bgs|beckett|cgc|sgc)\b/,
  /\bgraded\b/,
  /\bslabs?\b/,
  /\b(?:damaged|creased|heavily played|poor condition)\b/,
  /\breprint\b/,
  /\bdigital\b/,
  /\b(?:pick your|you pick|choose your|pick a card|complete your set|build your deck)\b/,
];

/**
 * Language markers. The English feed rejects any of them; the Japanese feed
 * REQUIRES a Japanese marker, so the two series can never sample each other.
 */
const NON_ENGLISH = /\b(?:japanese|korean|chinese|jpn|kor|kr|cn)\b|日本語|中国語|한국어|パルワールド|帕鲁|팰월드/;
const JAPANESE_MARKER = /\b(?:japanese|korean|chinese|jpn|jp|kor|kr|cn)\b|日本語|中国語|한국어|パルワールド|帕鲁|팰월드/;



/**
 * Rarity/variant tokens printed as a code suffix (EBP01-025OSR). A listing that
 * advertises a different variant is a different card, so it must never be
 * sampled into this card's price.
 */
const VARIANT_TOKENS = ["ssp", "osr", "tsp", "tsr", "sss", "sr", "sp", "rr", "pr", "td"];

/** The variant suffix of a printed code, or "" for the base printing. */
function variantSuffix(code: string): string {
  const m = /^e?[a-z]+\d+-\d+([a-z]+)$/i.exec(code.trim());
  return (m?.[1] ?? "").toLowerCase();
}

/** Printed number without a language prefix or parallel suffix. */
function printingRoot(code: string): string {
  return code.toLowerCase().replace(/^e/, "").replace(/(ssp|osr|tsp|tsr|sss|sr|sp|rr|pr|td)$/i, "");
}

/**
 * Printings whose card name is not a usable search term on its own. "Soul" is
 * the worst case: it appears in a huge share of Palworld card listings, so a
 * name match published eBay prices for ESOUL promos that have no listing at all.
 * These printings require the printed code in the listing title, full stop.
 */
const GENERIC_NAMES = new Set(["soul", "counter", "life counter", "material & ingredient counter"]);

function needsExactCode(opts: { code: string; name: string }): boolean {
  const name = opts.name.trim().toLowerCase();
  return (
    GENERIC_NAMES.has(name) ||
    name.length <= 4 ||
    /^(esoul|epr|life-|material-)/.test(opts.code.trim().toLowerCase())
  );
}

/** Reject a named set family when it conflicts with the requested printing. */
function hasConflictingFamily(title: string, code: string): boolean {
  const t = title.toLowerCase();
  const wanted = code.toLowerCase();
  if (/\bsoul(?:\s+promo)?\b/.test(t) && !wanted.includes("soul")) return true;
  if (/\btrial deck\b/.test(t) && !wanted.includes("td")) return true;
  if (/\bdawn of palpagos\b/.test(t) && !wanted.includes("bp01")) return true;
  return false;
}

/**
 * True when a marketplace listing title plausibly describes ONE raw copy of the
 * given card. Requires the printed number or the Pal name plus "palworld".
 */
export function isSingleCardListing(
  title: string,
  opts: { code: string; base: string; name: string; rarity?: string; language?: "en" | "jp" },
): boolean {
  const t = title.toLowerCase();
  if (!t.includes("palworld") && !t.includes("パルワールド")) return false;
  if (REJECT_PATTERNS.some((re) => re.test(t))) return false;
  if (opts.language === "jp") {
    if (!JAPANESE_MARKER.test(title.toLowerCase())) return false;
  } else if (NON_ENGLISH.test(t)) return false;


  const code = opts.code.toLowerCase();
  const bare = code.replace(/^e/, "");
  const name = opts.name.toLowerCase();
  const wanted = variantSuffix(opts.code);
  if (hasConflictingFamily(t, code)) return false;

  // A different printed number in the title means a different card.
  const printed = t.match(/e?[a-z]{2,8}\d{0,2}-\d{2,3}[a-z]*/g) ?? [];
  const codeHit = printed.some((p) => p === code || p === bare);
  const rootHit = printed.some((p) => printingRoot(p) === printingRoot(code));
  if (printed.length && !rootHit) return false;

  // Variant discipline: the parallel and the base printing trade at very
  // different levels, so the advertised variant has to match exactly.
  const advertised = VARIANT_TOKENS.filter((v) => new RegExp(`(^|[^a-z])${v}([^a-z]|$)`).test(t));
  const allowed = new Set([wanted, (opts.rarity ?? "").toLowerCase()].filter(Boolean));
  if (advertised.some((v) => !allowed.has(v))) return false;
  if (wanted && !codeHit && !advertised.includes(wanted)) return false;
  if (!wanted && advertised.some((v) => v !== (opts.rarity ?? "").toLowerCase())) return false;

  if (needsExactCode(opts)) return codeHit;
  return rootHit || t.includes(name);
}

/** Grading-company tokens that identify a certified slab listing. */
const GRADER_RE = /\b(?:psa|bgs|beckett|cgc|ace|tag|sgc)(?:\b|[-/])/;

/** Words that disqualify a graded listing even when a grader is named. */
const GRADED_REJECT = ["lot", "bundle", "playset", "proxy", "custom", "orica", "fan made", "fan-made", "sealed", "booster box", "sleeve", "binder", "japanese", "korean", "chinese", "grading service", "submission", "pick your", "you pick"];

/**
 * True when a listing title describes ONE certified (graded) copy of the card.
 * Graded copies are priced on a completely different curve to raw singles, so
 * they get their own feed rather than being thrown away or averaged in.
 */
export function isGradedCardListing(
  title: string,
  opts: { code: string; base: string; name: string; rarity?: string },
): boolean {
  const t = title.toLowerCase();
  if (!GRADER_RE.test(t) && !/\b(?:graded|slab|gem mint|pristine)\b/.test(t)) return false;
  if (GRADED_REJECT.some((w) => t.includes(w))) return false;

  const code = opts.code.toLowerCase();
  const bare = code.replace(/^e/, "");
  if (hasConflictingFamily(t, code)) return false;
  const printed = t.match(/e?[a-z]{2,8}\d{0,2}-\d{2,3}[a-z]*/g) ?? [];
  const codeHit = printed.some((p) => p === code || p === bare);
  const rootHit = printed.some((p) => printingRoot(p) === printingRoot(code));
  if (printed.length && !rootHit) return false;
  if (!codeHit && !t.includes("palworld") && !t.includes("パルワールド")) return false;

  const wanted = variantSuffix(opts.code);
  const advertised = VARIANT_TOKENS.filter(
    (v) => v !== "pr" && new RegExp(`(^|[^a-z])${v}([^a-z]|$)`).test(t),
  );
  const allowed = new Set([wanted, (opts.rarity ?? "").toLowerCase()].filter(Boolean));
  if (advertised.some((v) => !allowed.has(v))) return false;
  if (wanted && !codeHit && !advertised.includes(wanted)) return false;

  if (needsExactCode(opts)) return codeHit;
  return rootHit || t.includes(opts.name.toLowerCase());
}

export interface RobustQuote {
  market: number;
  low: number;
  high: number;
  count: number;
  /** Median absolute deviation as a share of the median — the noise level. */
  dispersion: number;
}

/**
 * Median-based aggregate with MAD outlier rejection. Means are useless here:
 * one mispriced $999 listing drags a $4 card into fantasy territory. We drop
 * anything more than 3 MADs from the median, then take the trimmed median of
 * what survives.
 */
export function robustQuote(values: number[], minSamples = 3): RobustQuote | null {
  const xs = values.filter((n) => Number.isFinite(n) && n > 0).sort((a, b) => a - b);
  if (xs.length < minSamples) return null;
  const median = (arr: number[]) => arr[Math.floor(arr.length / 2)] as number;
  // Percentile, not extreme. `low` used to be the single cheapest surviving
  // sample, so one mistyped or mismatched listing published a false "from"
  // price (the $60 ESOUL-002 case). p10/p90 describe where the market actually
  // sits and cannot be moved by a single listing.
  const pct = (arr: number[], p: number) =>
    round2(arr[Math.min(arr.length - 1, Math.max(0, Math.round((arr.length - 1) * p)))] as number);
  const m = median(xs);
  // Outlier rejection in log space: card prices are multiplicative, so a fixed
  // dollar band throws away real spread on cheap cards and keeps fantasy
  // listings on expensive ones.
  const logs = xs.map((x) => Math.log(x));
  const lm = median([...logs].sort((a, b) => a - b));
  const lmad = median(logs.map((x) => Math.abs(x - lm)).sort((a, b) => a - b)) || 0.25;
  const kept = xs.filter((x) => Math.abs(Math.log(x) - lm) <= Math.max(0.35, lmad * 3));
  const use = kept.length >= Math.max(minSamples, 3) ? kept : xs;
  const mid = median(use);
  const mad = median(use.map((x) => Math.abs(x - mid)).sort((a, b) => a - b)) || mid * 0.15;
  return {
    market: round2(mid),
    low: Math.min(pct(use, 0.1), round2(mid)),
    high: Math.max(pct(use, 0.9), round2(mid)),
    count: use.length,
    dispersion: Math.round((mad / (mid || 1)) * 100) / 100,
  };
}

/** Human freshness bucket for a live quote age in days. */
export type Freshness = "live" | "recent" | "stale" | "modelled";

export function freshnessOf(ageDays: number | null, hasLive: boolean): Freshness {
  if (!hasLive || ageDays === null) return "modelled";
  if (ageDays <= 0) return "live";
  if (ageDays <= MAX_QUOTE_AGE_DAYS) return "recent";
  return "stale";
}

export const FRESHNESS_META: Record<Freshness, { label: string; detail: string }> = {
  live: { label: "Updated today", detail: "Marketplace quotes captured in today's sync." },
  recent: { label: "Recent", detail: "Newest marketplace quote is 1-4 days old and still counts toward the market price." },
  stale: { label: "Stale", detail: "Newest marketplace quote is over 4 days old, so the shown price falls back to the Pal Stars Index." },
  modelled: { label: "Modelled", detail: "No validated marketplace quote — this is the transparent Pal Stars Index baseline." },
};

/**
 * Sanity band. The band exists to catch data errors (a $1 sticker, a $40,000
 * typo), NOT to hold the market down to the modelled baseline: a genuinely hot
 * chase card can trade many multiples above its printed-rarity anchor and that
 * has to be allowed through, otherwise the board silently under-reports it.
 *
 * Graded slabs are banded separately and far wider, because a certified
 * prototype/promo copy legitimately sells for thousands while raw copies of the
 * same printing sit in the low hundreds.
 */
export function isPlausibleQuote(usd: number, card: OfficialCard, graded = false): boolean {
  if (!Number.isFinite(usd) || usd < 0.05) return false;
  const anchor = indexValue(card);
  if (graded) return usd >= anchor / 3 && usd <= Math.max(2000, anchor * 400) && usd <= 250000;
  return usd >= anchor / 30 && usd <= Math.max(60, anchor * 45) && usd <= 60000;
}


/* -------------------------------------------------------- discrepancy flags */

export type PriceFlagKind =
  | "modelled"
  | "stale"
  | "thin-sample"
  | "source-spread"
  | "sold-below-ask"
  | "sold-above-ask"
  | "index-gap"
  | "graded-premium";

export interface PriceFlag {
  kind: PriceFlagKind;
  severity: "info" | "warn" | "high";
  label: string;
  detail: string;
}

export interface SourceQuote {
  source: string;
  day: string;
  market: number | null;
  low: number | null;
  high: number | null;
  soldAvg: number | null;
  soldCount: number;
  listings: number;
  /** Days behind today. */
  ageDays: number;
  /** Newest completed-sale timestamp behind this quote, when the feed reports one. */
  soldLastAt: string | null;
  /** False when the quote failed the sanity band and is shown but not blended. */
  trusted: boolean;
  /** True when the quote covers certified slabs rather than raw singles. */
  graded: boolean;
}

export interface PricePoint {
  day: string;
  value: number;
  /** True when the point came from a stored marketplace snapshot for that day
   * rather than the modelled Pal Stars Index baseline. */
  real?: boolean;
  /** Listings sampled across every trusted source on that day (graph columns). */
  listings?: number;
}

/** One stored completed-sales aggregate for a UTC day. */
export interface SoldPoint {
  day: string;
  value: number;
  count: number;
}

/**
 * Collapses stored snapshots into one recorded value per UTC day: the median of
 * every trusted source quote captured that day. This is what turns the daily
 * sync into an actual price history instead of a single "today" number.
 */
export function recordedHistory(snapshots: LiveSnapshot[], plausible: (n: number) => boolean): PricePoint[] {
  const byDay = new Map<string, Map<string, number>>();
  // Per-day sampled listing depth, one vote per source, drawn as graph columns.
  const volByDay = new Map<string, Map<string, number>>();
  for (const s of snapshots) {
    if (s.source === INDEX_SOURCE_ID) continue;
    const value = typeof s.market === "number" && s.market > 0 ? round2(s.market) : null;
    if (value === null || !plausible(value)) continue;
    const perSource = byDay.get(s.day) ?? new Map<string, number>();
    // One vote per source per day; last write wins for duplicate rows.
    perSource.set(s.source, value);
    byDay.set(s.day, perSource);
    const perVol = volByDay.get(s.day) ?? new Map<string, number>();
    perVol.set(s.source, Math.max(0, s.listings ?? 0));
    volByDay.set(s.day, perVol);
  }
  return [...byDay.entries()]
    .map(([day, perSource]): PricePoint | null => {
      const q = robustQuote([...perSource.values()], 1);
      if (!q) return null;
      const listings = [...(volByDay.get(day)?.values() ?? [])].reduce((a, b) => a + b, 0);
      return { day, value: q.market, real: true, listings };
    })
    .filter((p): p is PricePoint => p !== null)
    .sort((a, b) => a.day.localeCompare(b.day));

}


/** 30-day modelled series ending today, anchored on the index value. */
export function indexHistory(c: OfficialCard, days = 30): PricePoint[] {
  const end = indexValue(c);
  const out: PricePoint[] = [];
  let v = end;
  for (let i = 0; i < days; i++) {
    out.push({ day: utcDay(-i), value: round2(Math.max(0.05, v)) });
    v = v / (1 + drift(c.code, utcDay(-i)) * 0.35);
  }
  return out.reverse();
}

export interface PriceRow {
  code: string;
  base: string;
  name: string;
  subtitle: string;
  set: string;
  rarity: OfficialRarity;
  color: OfficialCard["color"];
  type: OfficialCard["type"];
  image: string;
  /** Blended market value in USD. */
  market: number;
  low: number;
  high: number;
  soldAvg: number | null;
  /** Most recent completed sale reported by any sold feed. */
  soldLastAt: string | null;
  soldCount: number;
  listings: number;
  /** 7-day percentage change. */
  change7d: number;
  /** 30-day percentage change. */
  change30d: number;
  history: PricePoint[];
  /** How many days of the series come from stored marketplace snapshots. */
  recordedDays: number;
  /** Completed-sale average from TCGplayer only. */
  tcgSoldAvg: number | null;
  tcgSoldCount: number;
  /** False when a platform view kept the row on the baseline (no quote there). */
  platformTracked?: boolean;
  /** Per-platform asking market value and daily series, never blended. */
  ebayMarket: number | null;
  tcgMarket: number | null;
  ebayHistory: PricePoint[];
  tcgHistory: PricePoint[];
  /** TCGplayer completed-sale average and sample size for every stored day. */
  tcgSoldHistory: SoldPoint[];
  ebayListings: number;
  tcgListings: number;
  /** Median asking price of certified (graded) copies, null when none tracked. */
  gradedAvg: number | null;
  gradedLow: number | null;
  gradedHigh: number | null;

  /** Graded listings sampled behind gradedAvg. */
  gradedCount: number;
  /**
   * Median asking price of JAPANESE printings, null when none tracked. Kept out
   * of `market`, `low`, `high` and the index on purpose.
   */
  jpAvg: number | null;
  /** Japanese listings sampled behind jpAvg. */
  jpCount: number;
  /** Recorded Japanese-printing snapshots, one per stored day. */
  jpHistory: PricePoint[];



  /** Which live sources contributed, empty when purely modelled. */
  liveSources: string[];
  /** Transparent baseline, always shown next to the blended market value. */
  index: number;
  /** Confidence in the blended market value. */
  confidence: "modelled" | "low" | "medium" | "high";
  /** Per-source quotes behind the blend, newest first. */
  quotes: SourceQuote[];
  /** Age of the freshest live quote in days, null when purely modelled. */
  freshnessDays: number | null;
  flags: PriceFlag[];
}

export interface LiveSnapshot {
  card_code: string;
  source: string;
  day: string;
  market: number | null;
  low: number | null;
  high: number | null;
  sold_avg: number | null;
  sold_count: number | null;
  listings: number | null;
  sold_last_at?: string | null;
}

function pct(from: number, to: number): number {
  if (!from) return 0;
  return Math.round(((to - from) / from) * 1000) / 10;
}

function dayAge(day: string): number {
  const then = Date.parse(`${day}T00:00:00Z`);
  if (!Number.isFinite(then)) return 99;
  return Math.max(0, Math.round((Date.parse(`${utcDay()}T00:00:00Z`) - then) / 86400000));
}

function changeOverHistory(history: PricePoint[], back: number, useAvailableWindow = false): number {
  const last = history[history.length - 1];
  const first = history[0];
  if (!last || !first) return 0;
  const lastTime = Date.parse(`${last.day}T00:00:00Z`);
  const firstTime = Date.parse(`${first.day}T00:00:00Z`);
  if (!Number.isFinite(lastTime) || !Number.isFinite(firstTime)) return 0;
  if (lastTime - firstTime < back * 86_400_000) {
    return useAvailableWindow && lastTime > firstTime ? pct(first.value, last.value) : 0;
  }
  const target = lastTime - back * 86_400_000;
  let from: PricePoint | undefined;
  for (let i = history.length - 1; i >= 0; i -= 1) {
    const point = history[i];
    if (point && Date.parse(`${point.day}T00:00:00Z`) <= target) {
      from = point;
      break;
    }
  }
  return from ? pct(from.value, last.value) : 0;
}

/** Live quotes older than this stop moving the market price. */
export const MAX_QUOTE_AGE_DAYS = 4;

/**
 * Builds the price board. Live snapshots are validated, outlier-rejected and
 * blended by median; the modelled index fills every remaining gap and every
 * disagreement between sources is surfaced as a flag rather than averaged away.
 */
export function buildPriceRows(snapshots: LiveSnapshot[] = []): PriceRow[] {
  const latestBoardDay = snapshots.reduce((latest, s) => (s.day > latest ? s.day : latest), "");
  const bySource = new Map<string, LiveSnapshot[]>();
  for (const s of snapshots) {
    const key = s.card_code.toUpperCase();
    const list = bySource.get(key) ?? [];
    list.push(s);
    bySource.set(key, list);
  }

  return OFFICIAL_CARDS.filter((c) => c.image).map((c) => {
    const history = indexHistory(c);
    const modelled = history[history.length - 1].value;
    const raw = (bySource.get(c.code.toUpperCase()) ?? []).filter((s) => s.source !== INDEX_SOURCE_ID);
    // Newest quote per source only — one storefront, one vote.
    const newestBySource = new Map<string, LiveSnapshot>();
    for (const s of raw) {
      const prev = newestBySource.get(s.source);
      if (!prev || s.day > prev.day) newestBySource.set(s.source, s);
    }

    const quotes: SourceQuote[] = [...newestBySource.values()]
      .map((s) => {
        const market = typeof s.market === "number" && s.market > 0 ? round2(s.market) : null;
        return {
          source: s.source,
          day: s.day,
          market,
          low: typeof s.low === "number" && s.low > 0 ? round2(s.low) : null,
          high: typeof s.high === "number" && s.high > 0 ? round2(s.high) : null,
          soldAvg: typeof s.sold_avg === "number" && s.sold_avg > 0 ? round2(s.sold_avg) : null,
          soldCount: s.sold_count ?? 0,
          listings: s.listings ?? 0,
          soldLastAt: s.sold_last_at ?? null,
          ageDays: dayAge(s.day),
          graded: isGradedSource(s.source),
          trusted: market === null ? false : isPlausibleQuote(market, c, isGradedSource(s.source)),
        };
      })
      .sort((a, b) => a.ageDays - b.ageDays || a.source.localeCompare(b.source));

    const fresh = quotes.filter(
      (q) => q.trusted && q.ageDays <= MAX_QUOTE_AGE_DAYS && q.market !== null,
    );
    // Raw ENGLISH singles set the market price; certified slabs and Japanese
    // printings are reported as their own series.
    const usable = fresh.filter((q) => !q.graded && !isJapaneseSource(q.source) && !isSoldSource(q.source));
    const soldQuotes = fresh.filter(
      (q) => !q.graded && !isJapaneseSource(q.source) && isSoldSource(q.source) && q.soldAvg !== null,
    );
    const gradedQuotes = fresh.filter((q) => q.graded);
    const jpQuotes = fresh.filter((q) => isJapaneseSource(q.source));
    const jpBlend = robustQuote(jpQuotes.map((q) => q.market as number), 1);

    const marketQuotes = usable.map((q) => q.market as number);
    // Depth-weighted blend: a quote built from 20 listings should outvote a
    // quote built from 3. A plain median of two sources is just their average,
    // which let a thin, optimistic storefront set the headline price.
    const weighted = usable.flatMap((q) =>
      Array.from({ length: Math.max(1, Math.min(6, q.listings + q.soldCount)) }, () => q.market as number),
    );
    const blend = robustQuote(weighted, 1);
    const market = blend ? blend.market : modelled;

    const gradedBlend = robustQuote(gradedQuotes.map((q) => q.market as number), 1);
    const gradedAvg = gradedBlend ? gradedBlend.market : null;

    const gradedLow = gradedQuotes.map((q) => q.low).filter((n): n is number => n !== null);
    const gradedHigh = gradedQuotes.map((q) => q.high).filter((n): n is number => n !== null);
    const gradedCount = gradedQuotes.reduce((a, q) => a + q.listings + q.soldCount, 0);

    // Displayed Low/High must describe the same card as Market. A stored low of
    // $60 against a $1,200 median is not a bargain, it is a mismatched listing
    // that slipped a title check, so a band that far from the median is dropped
    // rather than published as a "from" price users would chase.
    const inBand = (n: number) => n >= market / 4 && n <= market * 4;
    const lows = usable.map((q) => q.low).filter((n): n is number => n !== null && inBand(n));
    const highs = usable.map((q) => q.high).filter((n): n is number => n !== null && inBand(n));

    const soldAvg = soldQuotes.length
      ? (robustQuote(soldQuotes.map((q) => q.soldAvg as number), 1)?.market ?? null)
      : null;
    /* ---- per-platform series: eBay and TCGplayer are never blended ---- */
    const platformSold = (platform: PricePlatform) => {
      const qs = soldQuotes.filter((q) => sourcePlatform(q.source) === platform);
      return {
        avg: qs.length ? (robustQuote(qs.map((q) => q.soldAvg as number), 1)?.market ?? null) : null,
        count: qs.reduce((a, q) => a + q.soldCount, 0),
      };
    };
    const platformMarket = (platform: PricePlatform) => {
      const qs = usable.filter((q) => sourcePlatform(q.source) === platform && q.market !== null);
      return qs.length ? (robustQuote(qs.map((q) => q.market as number), 1)?.market ?? null) : null;
    };
    const platformHistory = (platform: PricePlatform) => {
      return recordedHistory(
        raw.filter(
          (s) =>
            sourcePlatform(s.source) === platform &&
            !isSoldSource(s.source) &&
            !isGradedSource(s.source) &&
            !isJapaneseSource(s.source),
        ),
        (n) => isPlausibleQuote(n, c),
      );
    };
    const tcgSold = platformSold("tcgplayer");
    const tcgSoldHistory: SoldPoint[] = raw
      .filter((s) => s.source === "tcgplayer-sold" && typeof s.sold_avg === "number" && s.sold_avg > 0)
      .map((s) => ({ day: s.day, value: round2(s.sold_avg as number), count: Math.max(0, s.sold_count ?? 0) }))
      .filter((p) => isPlausibleQuote(p.value, c))
      .sort((a, b) => a.day.localeCompare(b.day));


    // Real recorded snapshots win over the modelled baseline on any day the
    // sync captured a trusted quote; today's point always shows the live blend.
    const recorded = recordedHistory(
      raw.filter((s) => !isSoldSource(s.source) && !isGradedSource(s.source) && !isJapaneseSource(s.source)),
      (n) => isPlausibleQuote(n, c),
    );
    const recordedByDay = new Map(recorded.map((p) => [p.day, p]));
    // Graphs and movement metrics use stored marketplace history only. The
    // modelled baseline remains today's fallback value, but is never inserted
    // between real observations because that fabricated jumps and hid history.
    const merged = [...recordedByDay.values()].sort((a, b) => a.day.localeCompare(b.day));
    const chartHistory = merged.length ? merged : history;
    const recordedDays = merged.length;


    const soldCount = soldQuotes.reduce((a, q) => a + q.soldCount, 0);
    const listings = usable.reduce((a, q) => a + q.listings, 0);
    const freshnessDays = quotes.length ? quotes[0].ageDays : null;

    /* ---- flags: everything a buyer should double-check before paying ---- */
    const flags: PriceFlag[] = [];
    if (!usable.length) {
      flags.push({
        kind: "modelled",
        severity: "info",
        label: "Modelled price",
        detail: "No validated marketplace quote in the last 4 days — this is the Pal Stars Index baseline from printed rarity and demand.",
      });
    }
    if (quotes.length && freshnessDays !== null && freshnessDays > MAX_QUOTE_AGE_DAYS) {
      flags.push({
        kind: "stale",
        severity: "warn",
        label: `Stale feed · ${freshnessDays}d`,
        detail: `The freshest marketplace quote is ${freshnessDays} days old, so it no longer moves the market price.`,
      });
    }
    // Thin markets are where a board misleads most: five listings on a chase
    // parallel can put the median hundreds of dollars away from where copies
    // actually change hands, so shallow samples are labelled instead of being
    // presented with the same authority as a deep one.
    const samples = soldCount + listings;
    if (usable.length > 0 && samples < 8) {
      flags.push({
        kind: "thin-sample",
        severity: samples < 4 ? "high" : "warn",
        label: `Thin sample · ${samples}`,
        detail: `Only ${samples} validated listing(s) across ${usable.length} source(s). On a thin card the median can sit well away from the price copies actually trade at — open the card and check the live listings before you buy or sell.`,
      });
    }
    if (blend && marketQuotes.length >= 2) {
      const lo = Math.min(...marketQuotes);
      const hi = Math.max(...marketQuotes);
      const spread = Math.round(((hi - lo) / (lo || 1)) * 100);
      if (spread >= 40) {
        flags.push({
          kind: "source-spread",
          severity: spread >= 100 ? "high" : "warn",
          label: `Sources disagree ${spread}%`,
          detail: `Quotes range ${formatUsd(lo)}–${formatUsd(hi)} across ${marketQuotes.length} marketplaces. The median is used, not the average.`,
        });
      }
    }
    if (blend) {
      const gap = Math.round(((market - modelled) / (modelled || 1)) * 100);
      if (Math.abs(gap) >= 60) {
        flags.push({
          kind: "index-gap",
          severity: Math.abs(gap) >= 150 ? "high" : "warn",
          label: `${gap > 0 ? "+" : ""}${gap}% vs index`,
           detail: `Live market ${formatUsd(market)} against the ${formatUsd(modelled)} rarity baseline. Check the active listings before buying or selling.`,
        });
      }
    }
    if (gradedAvg !== null) {
      const mult = Math.round((gradedAvg / (market || 1)) * 10) / 10;
      flags.push({
        kind: "graded-premium",
        severity: "info",
        label: `Graded ${formatUsd(gradedAvg)}${mult > 1 ? ` · ${mult}x raw` : ""}`,
        detail: `Certified copies (PSA/BGS/CGC) are listed around ${formatUsd(gradedAvg)} across ${gradedCount} slab listing(s). Graded prices are tracked separately and never blended into the raw market value of ${formatUsd(market)}.`,
      });
    }



    const confidence: PriceRow["confidence"] = !usable.length
      ? "modelled"
      : soldCount >= 5 && usable.length >= 2
        ? "high"
        : usable.length >= 2 || soldCount >= 3
          ? "medium"
          : "low";

    return {
      code: c.code,
      base: c.base,
      name: c.name,
      subtitle: c.subtitle,
      set: c.set,
      rarity: c.rarity,
      color: c.color,
      type: c.type,
      image: c.image,
      market,
      low: lows.length ? round2(Math.min(...lows)) : round2(market * 0.78),
      high: highs.length ? round2(Math.max(...highs)) : round2(market * 1.42),
      soldAvg,
      soldLastAt:
        soldQuotes
          .map((q) => q.soldLastAt)
          .filter((d): d is string => Boolean(d))
          .sort()
          .pop() ?? null,
      soldCount,
      tcgSoldAvg: tcgSold.avg,
      tcgSoldCount: tcgSold.count,
      ebayMarket: platformMarket("ebay"),
      tcgMarket: platformMarket("tcgplayer"),
      ebayHistory: platformHistory("ebay"),
      tcgHistory: platformHistory("tcgplayer"),
      tcgSoldHistory,
      ebayListings: usable
        .filter((q) => sourcePlatform(q.source) === "ebay")
        .reduce((sum, q) => sum + q.listings, 0),
      tcgListings: usable
        .filter((q) => sourcePlatform(q.source) === "tcgplayer")
        .reduce((sum, q) => sum + q.listings, 0),

      listings,
      change7d: changeOverHistory(merged, 7),
      change30d: changeOverHistory(merged, 30, true),
      history: chartHistory,
      recordedDays,
      gradedAvg,
      gradedLow: gradedLow.length ? round2(Math.min(...gradedLow)) : null,
      gradedHigh: gradedHigh.length ? round2(Math.max(...gradedHigh)) : null,
      gradedCount,
      jpAvg: jpBlend ? jpBlend.market : null,
      jpCount: jpQuotes.reduce((a, q) => a + q.listings, 0),
      jpHistory: recordedHistory(
        raw.filter((s) => isJapaneseSource(s.source)),
        (n) => n > 0,
      ),



      liveSources: usable.map((q) => q.source),
      index: modelled,
      confidence,
      quotes,
      freshnessDays,
      flags,
    };
  });
}

/** Source id used for the modelled baseline rows. */
export const INDEX_SOURCE_ID = "palstars-index";

export function formatUsd(n: number): string {
  if (n >= 100) return `$${n.toFixed(0)}`;
  return `$${n.toFixed(2)}`;
}

/** Currency display options. Rates are indicative and shown as such. */
export const CURRENCIES = [
  { code: "USD", symbol: "$", rate: 1 },
  { code: "EUR", symbol: "€", rate: 0.92 },
  { code: "GBP", symbol: "£", rate: 0.78 },
  { code: "JPY", symbol: "¥", rate: 152 },
] as const;

export type CurrencyCode = (typeof CURRENCIES)[number]["code"];

export function formatMoney(usd: number, code: CurrencyCode): string {
  const c = CURRENCIES.find((x) => x.code === code)!;
  const v = usd * c.rate;
  if (code === "JPY") return `${c.symbol}${Math.round(v).toLocaleString()}`;
  return `${c.symbol}${v >= 100 ? v.toFixed(0) : v.toFixed(2)}`;
}

/** Total tracked cards, used in copy and structured data. */
export const TRACKED_CARD_COUNT = OFFICIAL_CARDS.filter((c) => c.image).length;


/**
 * Re-expresses the board on ONE platform, for the eBay / TCGplayer index toggle.
 *
 * Market value, daily history and the sold column are swapped for that
 * platform's own stored series; rows with no series on that platform drop out.
 * The two platforms are deliberately never merged: buyer behaviour on eBay
 * (auctions, international sellers) and on TCGplayer (fixed-price seller stock)
 * produces different levels, and averaging them would publish a number that
 * matches neither market.
 */
export function withPlatform(
  rows: PriceRow[],
  platform: PricePlatform,
  keepUntracked = false,
): PriceRow[] {
  return rows
    .map((r) => {
      const history = platform === "ebay" ? r.ebayHistory : r.tcgHistory;
      const market = platform === "ebay" ? r.ebayMarket : r.tcgMarket;
      const soldAvg = platform === "ebay" ? null : r.tcgSoldAvg;
      const soldCount = platform === "ebay" ? 0 : r.tcgSoldCount;
      if (history.length < 1 || market === null || market <= 0) {
        // Untracked on this platform. Dropping the row hid real printings such
        // as ETD01-012TSP from the list entirely, so optionally keep it on the
        // deterministic index baseline and label it modelled — never as if the
        // platform had quoted it.
        if (!keepUntracked) return null;
        return {
          ...r,
          market: r.index,
          history: r.history.map((p) => ({ ...p, real: false })),
          recordedDays: 0,
          soldAvg: null,
          soldCount: 0,
          listings: 0,
          liveSources: [],
          confidence: "modelled",
          platformTracked: false,
        } satisfies PriceRow;
      }
      const last = history[history.length - 1].value;
      // Per-marketplace histories are ragged (each card starts on the day that
      // feed first quoted it), so the windows are measured in CALENDAR DAYS.
      // A card without enough dated history reports 0 rather than passing its
      // whole-life move off as a 7-day or 30-day move in the movers lists.
      return {
        ...r,
        market,
        history,
        recordedDays: history.filter((p) => p.real).length,
        soldAvg,
        soldCount,
        listings: platform === "ebay" ? r.ebayListings : r.tcgListings,
        change7d: changeOverHistory(history, 7),
        change30d: changeOverHistory(history, 30, true),
        liveSources: r.liveSources.filter((s) => sourcePlatform(s) === platform),

      } satisfies PriceRow;
    })
    .filter((r): r is PriceRow => r !== null);
}
